"use client"

import Form from 'next/form'
import { useActionState } from "react";
import { login, LoginState } from '@/services/login/action'
import { Mail, Lock, Shield } from 'lucide-react'
import Image from 'next/image'
import Button from '@/components/ui/button/Button'
import Alert from '@/components/ui/alert/Alert'

export default function LoginPage() {
    const [state, action, pending] = useActionState(login, {} as LoginState)

    return (
        <div className="w-full">
            {/* Logo and Header */}
            <div className="text-center mb-8">
                <div className="flex justify-center mb-6">
                    <Image
                        src="/images/logo/logo.png"
                        alt="Admin Logo"
                        width={120}
                        height={48}
                        className="dark:invert"
                    />
                </div>
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-white/90 mb-2">
                    Welcome back
                </h1>
                <p className="text-gray-500 dark:text-gray-400">
                    Sign in to your admin account
                </p>
            </div>

            {/* Login Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-theme-lg p-8">
                {/* Display form errors */}
                {state.errors?._form && (
                    <div className="mb-6">
                        <Alert
                            variant="error"
                            title="Authentication Error"
                            message={state.errors._form[0]}
                        />
                    </div>
                )}
                
                <Form action={action} className="space-y-6">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email Address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                required
                                placeholder="Enter your email"
                                className={`w-full pl-10 pr-4 py-3 rounded-lg border text-gray-900 dark:text-white/90 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 transition-colors ${
                                    state.errors?.email 
                                        ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20' 
                                        : 'border-gray-300 dark:border-gray-600 focus:border-brand-500 focus:ring-brand-500/20'
                                } focus:outline-none focus:ring-4`}
                            />
                        </div>
                        {state.errors?.email && (
                            <p className="mt-2 text-sm text-error-600 dark:text-error-400">
                                {state.errors.email[0]}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                required
                                placeholder="Enter your password"
                                className={`w-full pl-10 pr-4 py-3 rounded-lg border text-gray-900 dark:text-white/90 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 transition-colors ${
                                    state.errors?.password 
                                        ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20' 
                                        : 'border-gray-300 dark:border-gray-600 focus:border-brand-500 focus:ring-brand-500/20'
                                } focus:outline-none focus:ring-4`}
                            />
                        </div>
                        {state.errors?.password && (
                            <p className="mt-2 text-sm text-error-600 dark:text-error-400">
                                {state.errors.password[0]}
                            </p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        disabled={pending}
                        className="w-full"
                        startIcon={<Shield className="h-5 w-5" />}
                    >
                        {pending ? 'Signing in...' : 'Sign In'}
                    </Button>
                </Form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                        <Shield className="h-4 w-4 mr-2" />
                        Secure admin access
                    </div>
                </div>
            </div>
        </div>
    )
}
